import type {
  CogniteClient,
  InstanceType,
  PropertyValueGroupV3,
  RawPropertyValueV3,
  ViewDefinition,
  ViewDefinitionProperty,
  ViewPropertyDefinition,
  ViewReference,
} from '@cognite/sdk';

import {
  INFIELD_SOURCE_SYSTEM_EXTERNAL_ID_V_1,
  INFIELD_SOURCE_SYSTEM_NODE_PROPERTIES,
  INFIELD_SOURCE_SYSTEM_VIEW,
} from '../../../constants/cdm-infield-data-model';
import type {
  NodeRef,
  NodeWithProperties,
  PropertyRecord,
  UpsertNodeInstanceItem,
  UpsertNodeInstanceProps,
} from '../types';
import { isNodeRef } from '../utils/node-ref';
import { assertPropertyValueGroupV3 } from '../utils/property-value-type-guards';

import type { IViewService } from './view-service.interface';

type NodeInstance = {
  viewDefinition: ViewDefinition;
  properties: PropertyRecord;
  externalId: string;
};

type NodeUpsertItem = {
  instanceType: 'node';
  externalId: string;
  space: string;
  sources: Array<{
    source: ViewReference;
    properties: PropertyValueGroupV3;
  }>;
};

type InfieldSourceSystemRef = { externalId: string; space: string };

export interface CogniteClientDependencies {
  views: {
    retrieve: CogniteClient['views']['retrieve'];
  };
  instances: {
    retrieve: CogniteClient['instances']['retrieve'];
    inspect: CogniteClient['instances']['inspect'];
    upsert: CogniteClient['instances']['upsert'];
    delete: CogniteClient['instances']['delete'];
  };
}

const isViewPropertyDefinition = (
  property: ViewDefinitionProperty
): property is ViewPropertyDefinition => {
  return 'container' in property;
};

const isDirectRelationProperty = (
  property: ViewDefinitionProperty
): property is ViewPropertyDefinition =>
  isViewPropertyDefinition(property) && property.type.type === 'direct';

/**
 * Direct relations are stored as `{ space, externalId }`. Display code may add
 * fields such as `name`; drop those before upsert.
 */
const toDirectRelationWriteValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(toDirectRelationWriteValue);
  }

  if (isNodeRef(value)) {
    return { space: value.space, externalId: value.externalId };
  }

  return value;
};

const stripDirectRelationWriteValues = (
  propertyRecord: PropertyRecord,
  viewDefinition: ViewDefinition
): PropertyRecord => {
  const directRelationPropertyIds = new Set(
    Object.entries(viewDefinition.properties)
      .filter(([, property]) => isDirectRelationProperty(property))
      .map(([propertyId]) => propertyId)
  );

  return Object.fromEntries(
    Object.entries(propertyRecord).map(([propertyId, value]) => [
      propertyId,
      directRelationPropertyIds.has(propertyId)
        ? toDirectRelationWriteValue(value)
        : value,
    ])
  );
};

export const findViewPropertyByContainer = (
  properties: Record<string, ViewDefinitionProperty>,
  containerExternalId: string,
  containerPropertyIdentifier: string
): { id: string; property: ViewPropertyDefinition } | undefined => {
  const foundProp = Object.entries(properties).find(
    ([_, property]) =>
      isViewPropertyDefinition(property) &&
      property.container.externalId === containerExternalId &&
      property.containerPropertyIdentifier === containerPropertyIdentifier
  );
  if (!foundProp) return undefined;
  const [id, property] = foundProp;
  return isViewPropertyDefinition(property) ? { id, property } : undefined;
};

export class ViewService implements IViewService {
  private cogniteClient: CogniteClientDependencies;

  constructor(client: CogniteClientDependencies) {
    this.cogniteClient = client;
  }

  public async fetchViewDefinition(
    viewReference: ViewReference
  ): Promise<ViewDefinition | undefined> {
    const allRelevantViews = await this.cogniteClient.views.retrieve([
      {
        externalId: viewReference.externalId,
        space: viewReference.space,
        version: viewReference.version,
      },
    ]);

    return allRelevantViews.items[0];
  }

  public async fetchNodesWithProperties(
    nodes: NodeRef[],
    viewReference: ViewReference
  ): Promise<NodeWithProperties[]> {
    if (nodes.length === 0) {
      return [];
    }

    const nodeReferences: {
      instanceType: InstanceType;
      externalId: string;
      space: string;
    }[] = nodes.map((node) => ({
      instanceType: 'node',
      externalId: node.externalId,
      space: node.space,
    }));

    const { items } = await this.cogniteClient.instances.retrieve({
      items: nodeReferences,
      sources: [
        {
          source: viewReference,
        },
      ],
    });

    return items.map((item) => {
      const properties = this.getNodeProperties(item, viewReference);
      return {
        externalId: item.externalId,
        space: item.space,
        properties,
      };
    });
  }

  public async fetchNodeInstance(
    space: string,
    externalId: string
  ): Promise<NodeInstance | undefined> {
    /**
     * NOTE(eiriklv): We first need to figure out
     * which view to fetch the node through, to
     * get the correct properties, so we inspect
     * the node to get that information
     */
    const inspectResults = await this.cogniteClient.instances.inspect({
      inspectionOperations: {
        involvedViews: { allVersions: false },
        involvedContainers: { allVersions: false },
      },
      items: [{ externalId, space, instanceType: 'node' }],
    });

    /**
     * NOTE(eiriklv): We select the most specific view
     * that is involved with the node (which is the first one)
     */
    const selectedViewReference =
      inspectResults.items[0]?.inspectionResults.involvedViews?.[0];

    if (!selectedViewReference) {
      return undefined;
    }

    /**
     * NOTE(eiriklv): Fetch the node using the selected view reference
     */
    const nodeWithProperties = await this.fetchNodesWithProperties(
      [{ externalId, space }],
      selectedViewReference
    );

    const firstResult = nodeWithProperties[0];

    if (!firstResult) {
      return undefined;
    }

    /**
     * NOTE(eiriklv): Fetch the view definition for the selected view
     */
    const selectedViewDefinition = await this.fetchViewDefinition(
      selectedViewReference
    );

    if (!selectedViewDefinition) {
      return undefined;
    }

    return {
      viewDefinition: selectedViewDefinition,
      properties: firstResult.properties,
      externalId,
    };
  }

  /**
   * Creates a new instance of a specified view type based on the input values.
   *
   * This method:
   * - Formats the given property record into a structure suitable for upsert.
   * - Automatically fills out supported `CogniteSourceable` properties if they exist in the view definition.
   * - Upserts the provided instance.
   *
   * @param externalId - The identifier of the node instance.
   * @param space - The space where the node instance will be stored (will likely differ from the view definition space).
   * @param viewReference - The view type through which the properties are ingested.
   * @param viewDefinition - The view definition containing metadata and property structure.
   * @param propertyRecord - The user-provided property values for the node.
   * @returns A promise resolving to the externalId and space of the created or updated node.
   */
  public async upsertNodeInstance({
    externalId,
    space,
    viewReference,
    viewDefinition,
    propertyRecord,
    userId,
  }: UpsertNodeInstanceProps): Promise<{ externalId: string; space: string }> {
    const sourceSystemBySpace = new Map<string, InfieldSourceSystemRef>();
    const item = await this.buildNodeUpsertItem(
      {
        externalId,
        space,
        viewReference,
        viewDefinition,
        propertyRecord,
        userId,
      },
      sourceSystemBySpace
    );

    await this.cogniteClient.instances.upsert({ items: [item] });

    return { externalId, space };
  }

  public async upsertNodeInstances(
    nodeInstances: UpsertNodeInstanceItem[],
    userId?: string
  ): Promise<Array<{ externalId: string; space: string }>> {
    if (nodeInstances.length === 0) {
      return [];
    }

    const sourceSystemBySpace = new Map<string, InfieldSourceSystemRef>();
    const items: NodeUpsertItem[] = [];

    for (const instance of nodeInstances) {
      items.push(
        await this.buildNodeUpsertItem(
          { ...instance, userId },
          sourceSystemBySpace
        )
      );
    }

    await this.cogniteClient.instances.upsert({ items });

    return nodeInstances.map(({ externalId, space }) => ({
      externalId,
      space,
    }));
  }

  private async buildNodeUpsertItem(
    {
      externalId,
      space,
      viewReference,
      viewDefinition,
      propertyRecord,
      userId,
    }: UpsertNodeInstanceProps,
    sourceSystemBySpace: Map<string, InfieldSourceSystemRef>
  ): Promise<NodeUpsertItem> {
    const formattedPropertyValues = assertPropertyValueGroupV3(
      stripDirectRelationWriteValues(propertyRecord, viewDefinition)
    );

    /**
     * NOTE(eiriklv): Find any relevant CogniteSourceable
     * properties that exist in the view definition, so
     * that we can fill them out accordingly
     */
    const cogniteSourceableSourceCreatedTime = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceCreatedTime'
    );

    const cogniteSourceableSourceUpdatedTime = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceUpdatedTime'
    );

    /**
     * NOTE(eiriklv): Add custom handling for any existing
     * CogniteSourceable properties that are part of the
     * view definition. Fill out only the ones that exist,
     * but always fill them out automatically (they will always be hidden in the form)
     *
     * TODO(FO-1307): Add handling for the remaining CogniteSourceable
     * properties that are not yet covered
     */

    const cogniteSourceableSourceCreatedUser = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceCreatedUser'
    );

    const cogniteSourceableSourceUpdatedUser = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'sourceUpdatedUser'
    );

    const cogniteSourceableSource = findViewPropertyByContainer(
      viewDefinition.properties,
      'CogniteSourceable',
      'source'
    );

    let sourcePropDirectRelation: RawPropertyValueV3 | undefined =
      cogniteSourceableSource
        ? formattedPropertyValues[cogniteSourceableSource.id]
        : undefined;

    if (cogniteSourceableSource && !sourcePropDirectRelation) {
      const cachedSourceSystem = sourceSystemBySpace.get(space);
      if (cachedSourceSystem) {
        sourcePropDirectRelation = cachedSourceSystem;
      } else {
        const createdSourceSystem = await this.createInfieldSourceSystem(space);
        sourceSystemBySpace.set(space, createdSourceSystem);
        sourcePropDirectRelation = createdSourceSystem;
      }
    }

    const sourcableProperties: PropertyValueGroupV3 = ((
      now = new Date().toJSON()
    ) => ({
      ...(cogniteSourceableSourceCreatedTime
        ? {
            [cogniteSourceableSourceCreatedTime.id]:
              formattedPropertyValues[cogniteSourceableSourceCreatedTime.id] ??
              now,
          }
        : {}),
      ...(cogniteSourceableSourceUpdatedTime
        ? {
            [cogniteSourceableSourceUpdatedTime.id]: now,
          }
        : {}),
      ...(cogniteSourceableSource && sourcePropDirectRelation
        ? {
            [cogniteSourceableSource.id]: sourcePropDirectRelation,
          }
        : {}),
      ...(cogniteSourceableSourceCreatedUser
        ? {
            [cogniteSourceableSourceCreatedUser.id]:
              formattedPropertyValues[cogniteSourceableSourceCreatedUser.id] ??
              userId,
          }
        : {}),
      ...(cogniteSourceableSourceUpdatedUser && userId
        ? {
            [cogniteSourceableSourceUpdatedUser.id]: userId,
          }
        : {}),
    }))();

    return {
      instanceType: 'node',
      externalId,
      space,
      sources: [
        {
          source: viewReference,
          properties: {
            ...formattedPropertyValues,
            ...sourcableProperties,
          },
        },
      ],
    };
  }

  /**
   * Deletes multiple node instances.
   * @param nodes - An array of nodes to delete, each with a space and externalId.
   */
  public async deleteNodeInstance(nodes: NodeRef[]): Promise<NodeRef[]> {
    if (!nodes.length) return [];

    await this.cogniteClient.instances.delete(
      nodes.map(({ space, externalId }) => ({
        instanceType: 'node',
        space,
        externalId,
      }))
    );
    return nodes;
  }

  private getNodeProperties(
    node: { properties?: Record<string, Record<string, unknown>> },
    viewReference: ViewReference
  ): PropertyRecord {
    const viewSpace = viewReference.space;
    const viewExternalId = viewReference.externalId;
    const viewVersion = viewReference.version;
    const viewIdentity = `${viewExternalId}/${viewVersion}`;

    return { ...(node.properties?.[viewSpace]?.[viewIdentity] || {}) };
  }

  /**
   * This method creates and upserts a new instance of the Infield Source System node from a given space.
   * This is used to create a source system node for the Infield app when no source system is provided.
   * @param space - The space where the node instance will be stored (will likely differ from the view definition space).
   * @returns A promise resolving to the externalId and space of the source system node.
   */
  private async createInfieldSourceSystem(
    space: string
  ): Promise<{ externalId: string; space: string }> {
    await this.cogniteClient.instances.upsert({
      items: [
        {
          instanceType: 'node',
          externalId: INFIELD_SOURCE_SYSTEM_EXTERNAL_ID_V_1,
          space,
          // Setting existingVersion to 0 makes this behave like an insert-only operation.
          // If a node with this externalId already exists and if skipOnVersionConflict is true,
          // the operation will skip this item
          existingVersion: 0,
          sources: [
            {
              source: INFIELD_SOURCE_SYSTEM_VIEW,
              properties: INFIELD_SOURCE_SYSTEM_NODE_PROPERTIES,
            },
          ],
        },
      ],
      // If we try to create the source system when it already exists,
      // skip this item instead of failing the entire request.
      // This works in conjunction with existingVersion: 0.
      skipOnVersionConflict: true,
    });

    return {
      externalId: INFIELD_SOURCE_SYSTEM_EXTERNAL_ID_V_1,
      space,
    };
  }
}

export const createViewService = (
  client: CogniteClientDependencies
): IViewService => new ViewService(client);
